import React from "react";
import {
    Sheet,
    SheetContent,
    SheetHeader,
    SheetTitle,
} from "@/components/ui/sheet";
import {
    Popover,
    PopoverContent,
    PopoverTrigger,
} from "@/components/ui/popover";
import { Calendar } from "@/components/ui/calendar";
import { Button } from "@/components/ui/button";
import { useDayPass, INCLUDED_MASSAGE_VALUE } from "@/contexts/DayPassContext";
import { useLanguage } from "@/contexts/LanguageContext";
import { Minus, Plus, Calendar as CalendarIcon, Clock, User, Mail, Phone, X, ArrowRight, Check, ArrowLeft } from "lucide-react";
import { IconMassage } from "@tabler/icons-react";
import { motion, AnimatePresence } from "framer-motion";
import { translations } from "@/data/translations";
import { spaServices as services } from "@/data/spaServices";
import { extractDurationPricePairs, getSingleLanguageTitle } from "@/utils/spaUtils";
import { toast } from "sonner";
import { format } from "date-fns";
import {
    trackDayPassTrayOpen,
    trackDayPassTrayClose,
    trackDayPassGuestCountChange,
    trackDayPassViewNavigation,
    trackDayPassMassageUpgrade,
    trackDayPassFormStart,
    trackDayPassFormSubmit,
} from "@/utils/analytics";

type View = 'overview' | 'massageUpgrade' | 'bookingForm';

const MassageUpgradeItem = ({ service, guestIndex, onUpgradeSelect }: { service: typeof services[0]; guestIndex: number; onUpgradeSelect?: (treatmentId: string, treatmentName: string, treatmentPrice: number, upgradePrice: number) => void }) => {
    const { getGuestMassageUpgrade, setGuestMassageUpgrade } = useDayPass();
    const { language } = useLanguage();
    const t = (translations[language] as any)?.activities?.dayPass?.bookingTray || translations.en.activities?.dayPass?.bookingTray;
    
    const guestUpgrade = getGuestMassageUpgrade(guestIndex);

    // Only show massage category services
    if (service.category !== 'massage') return null;

    // Get service data from translations
    let serviceData: any;
    if (service.category === 'massage') {
        serviceData = (translations[language] as any)?.spa?.treatmentsAndServices?.therapeuticMassages?.[service.translationKey.split('.').pop() || ''];
    }

    const serviceName = getSingleLanguageTitle(serviceData?.name || serviceData?.title || service.translationKey, service.translationKey, language);
    const pricingStr = serviceData?.pricing || serviceData?.price || '';
    let durationPairs = extractDurationPricePairs(pricingStr);

    // Fallback for single-price items
    if (durationPairs.length === 0 && pricingStr) {
        const priceMatch = pricingStr.match(/(\d+)/);
        const durationMatch = pricingStr.match(/(\d+)\s*Min/);
        if (priceMatch) {
            durationPairs = [{
                duration: durationMatch ? durationMatch[1] : '60',
                price: priceMatch[1]
            }];
        }
    }

    return (
        <div className="space-y-3 pb-6 border-b border-white/5 last:border-0 last:pb-2 group">
            <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-xl bg-alpine-gold/10 flex items-center justify-center border border-alpine-gold/20 group-hover:border-alpine-gold/50 transition-colors">
                    <IconMassage className="w-5 h-5 text-alpine-gold" />
                </div>
                <div>
                    <h4 className="text-white font-playfair text-lg leading-tight">{serviceName}</h4>
                </div>
            </div>

            <p className="text-white/50 text-xs font-lato leading-relaxed px-1 line-clamp-2">
                {serviceData?.description || ''}
            </p>

            <div className="space-y-1.5 pt-1">
                {durationPairs.map((pair) => {
                    const treatmentId = `${service.serviceId}-${pair.duration}`;
                    const treatmentPrice = parseInt(pair.price);
                    const upgradePrice = Math.max(0, treatmentPrice - INCLUDED_MASSAGE_VALUE);
                    const isSelected = guestUpgrade.selected && guestUpgrade.treatmentId === treatmentId;

                    return (
                        <div
                            key={treatmentId}
                            className={`w-full flex items-center justify-between bg-white/5 border rounded-xl p-2.5 px-4 transition-all duration-300 group/item cursor-pointer ${
                                isSelected 
                                    ? 'border-alpine-gold bg-alpine-gold/10' 
                                    : 'border-white/10 hover:border-alpine-gold/30'
                            }`}
                            onClick={() => {
                                setGuestMassageUpgrade(guestIndex, {
                                    selected: true,
                                    treatmentId,
                                    treatmentName: `${serviceName} ${pair.duration} Min`,
                                    treatmentPrice,
                                    upgradePrice,
                                });
                                toast.success(t.upgradeSelected || 'Upgrade selected');
                                // Call tracking callback if provided
                                if (onUpgradeSelect) {
                                    onUpgradeSelect(treatmentId, `${serviceName} ${pair.duration} Min`, treatmentPrice, upgradePrice);
                                }
                            }}
                        >
                            <div className="flex items-baseline gap-3">
                                <span className="text-[10px] uppercase tracking-widest text-white/40 font-bold font-lato">
                                    {pair.duration} Min
                                </span>
                                <div className="flex flex-col">
                                    <span className="text-alpine-gold font-bold font-playfair text-base">
                                        CHF {treatmentPrice}
                                    </span>
                                    {upgradePrice > 0 && (
                                        <span className="text-white/50 text-[10px] font-lato">
                                            {t.upgradeFor || 'Upgrade for'} +CHF {upgradePrice}
                                        </span>
                                    )}
                                </div>
                            </div>
                            {isSelected && (
                                <div className="w-6 h-6 rounded-full bg-alpine-gold flex items-center justify-center">
                                    <Check className="w-4 h-4 text-black" />
                                </div>
                            )}
                        </div>
                    );
                })}
            </div>
        </div>
    );
};

export const DayPassBookingTray: React.FC = () => {
    const {
        numberOfGuests,
        incrementGuests,
        decrementGuests,
        guestMassageUpgrades,
        setGuestMassageUpgrade,
        clearGuestMassageUpgrade,
        clearAllMassageUpgrades,
        getGuestMassageUpgrade,
        formData,
        setFormData,
        resetFormData,
        basePassPrice,
        upgradePrice,
        totalPrice,
        isTrayOpen,
        traySource,
        setIsTrayOpen,
        resetAll,
    } = useDayPass();
    const { language } = useLanguage();
    const [view, setView] = React.useState<View>('overview');
    const [selectedGuestForUpgrade, setSelectedGuestForUpgrade] = React.useState<number | null>(null);
    const [isHovered, setIsHovered] = React.useState(false);
    const [isSubmitting, setIsSubmitting] = React.useState(false);
    const [submitStatus, setSubmitStatus] = React.useState<'idle' | 'success' | 'error'>('idle');
    const [trayOpenTime, setTrayOpenTime] = React.useState<number | null>(null);
    const formStartedRef = React.useRef<boolean>(false);

    const t = (translations[language] as any)?.activities?.dayPass?.bookingTray || translations.en.activities?.dayPass?.bookingTray || {};

    // Reset hover state when tray opens/closes
    React.useEffect(() => {
        setIsHovered(false);
    }, [isTrayOpen]);

    // Reset view when tray opens (tracking is handled in DayPassContext.setIsTrayOpen)
    React.useEffect(() => {
        if (isTrayOpen) {
            setView('overview');
            setSelectedGuestForUpgrade(null);
            setTrayOpenTime(Date.now());
            formStartedRef.current = false;
            // Note: tray open tracking is handled in DayPassContext.setIsTrayOpen to avoid duplicates
        }
    }, [isTrayOpen]);

    const handleTrayClose = (open: boolean) => {
        if (!open && trayOpenTime) {
            const timeSpentSeconds = Math.floor((Date.now() - trayOpenTime) / 1000);
            const hasUpgrades = guestMassageUpgrades.some(g => g.upgrade.selected);
            trackDayPassTrayClose(
                traySource, // Pass source from context
                view,
                timeSpentSeconds,
                numberOfGuests,
                hasUpgrades,
                formStartedRef.current
            );
            setTrayOpenTime(null);
            formStartedRef.current = false;
        }
        setIsTrayOpen(open);
    };

    const handleIncrementGuests = () => {
        const previousCount = numberOfGuests;
        incrementGuests();
        trackDayPassGuestCountChange('increment', previousCount, previousCount + 1, view);
    };

    const handleDecrementGuests = () => {
        const previousCount = numberOfGuests;
        decrementGuests();
        trackDayPassGuestCountChange('decrement', previousCount, previousCount - 1, view);
    };

    const handleViewChange = (newView: View) => {
        const fromView = view;
        const hasUpgrades = guestMassageUpgrades.some(g => g.upgrade.selected);
        
        // Track form start when navigating to booking form
        if (newView === 'bookingForm' && !formStartedRef.current) {
            formStartedRef.current = true;
            trackDayPassFormStart(
                numberOfGuests,
                hasUpgrades,
                totalPrice,
                basePassPrice,
                upgradePrice
            );
        }
        
        trackDayPassViewNavigation(fromView, newView, numberOfGuests, hasUpgrades);
        setView(newView);
    };

    const handleMassageUpgrade = (
        action: 'select' | 'clear',
        guestIndex: number,
        treatmentId?: string,
        treatmentName?: string,
        treatmentPrice?: number,
        upgradePrice?: number
    ) => {
        const totalUpgradePrice = guestMassageUpgrades.reduce((sum, g) => sum + (g.upgrade.upgradePrice || 0), 0);
        trackDayPassMassageUpgrade(
            action,
            guestIndex,
            treatmentId,
            treatmentName,
            treatmentPrice,
            upgradePrice,
            numberOfGuests,
            totalUpgradePrice
        );
    };

    const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
        const { name, value } = e.target;
        setFormData({ [name]: value });
    };

    const handleDateSelect = (date: Date | undefined) => {
        if (date) {
            const year = date.getFullYear();
            const month = String(date.getMonth() + 1).padStart(2, '0');
            const day = String(date.getDate()).padStart(2, '0');
            const dateStr = `${year}-${month}-${day}`;
            setFormData({ preferredDate: dateStr });
        }
    };

    const getTodayAtMidnight = () => {
        const today = new Date();
        today.setHours(0, 0, 0, 0);
        return today;
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();

        if (!formData.fullName || !formData.email || !formData.phone || !formData.preferredDate || !formData.preferredTime) {
            toast.error(t.fillAllFields || "Please fill in all required fields");
            return;
        }

        setIsSubmitting(true);
        setSubmitStatus('idle');

        try {
            const payload = {
                fullName: formData.fullName,
                email: formData.email,
                phone: formData.phone,
                preferredDate: formData.preferredDate,
                preferredTime: formData.preferredTime,
                numberOfGuests,
                basePassPrice: 200,
                guestMassageUpgrades: guestMassageUpgrades.map(g => ({
                    guestIndex: g.guestIndex,
                    upgrade: g.upgrade,
                })),
                totalPrice,
                specialRequests: formData.specialRequests || null,
                language,
                timestamp: new Date().toISOString(),
            };

            const response = await fetch("/api/day-pass-inquiry", {
                method: "POST",
                headers: {
                    "Content-Type": "application/json",
                },
                body: JSON.stringify(payload),
            });

            if (!response.ok) {
                const errorData = await response.json().catch(() => ({}));
                const errorMsg = errorData.error || t.submitError || "Failed to send inquiry. Please try again.";
                setSubmitStatus('error');
                
                // Track form submission error
                const hasUpgrades = guestMassageUpgrades.some(g => g.upgrade.selected);
                trackDayPassFormSubmit(
                    numberOfGuests,
                    hasUpgrades,
                    totalPrice,
                    basePassPrice,
                    upgradePrice,
                    formData.preferredTime || '',
                    !!formData.specialRequests,
                    'error'
                );
                
                toast.error(errorMsg);
                setIsSubmitting(false);
                return;
            }

            const result = await response.json();
            setSubmitStatus('success');
            
            // Track form submission success
            const hasUpgrades = guestMassageUpgrades.some(g => g.upgrade.selected);
            trackDayPassFormSubmit(
                numberOfGuests,
                hasUpgrades,
                totalPrice,
                basePassPrice,
                upgradePrice,
                formData.preferredTime || '',
                !!formData.specialRequests,
                'success'
            );
            
            resetAll();
            toast.success(t.successMessage || "Booking request sent successfully!");

            setTimeout(() => {
                handleTrayClose(false);
                setSubmitStatus('idle');
                setView('overview');
            }, 2000);

        } catch (error) {
            console.error("Day pass inquiry submission error:", error);
            setSubmitStatus('error');
            
            // Track form submission error
            const hasUpgrades = guestMassageUpgrades.some(g => g.upgrade.selected);
            trackDayPassFormSubmit(
                numberOfGuests,
                hasUpgrades,
                totalPrice,
                basePassPrice,
                upgradePrice,
                formData.preferredTime || '',
                !!formData.specialRequests,
                'error'
            );
            
            toast.error(t.networkError || "Network error. Please check your connection and try again.");
        } finally {
            setIsSubmitting(false);
        }
    };

    // Filter only massage services for upgrade view
    const massageServices = services.filter(s => s.category === 'massage');

    return (
        <>
            <AnimatePresence>
                {!isTrayOpen && (
                    <motion.button
                        initial={{ x: 100, opacity: 0 }}
                        animate={{ x: 0, opacity: 1 }}
                        exit={{ x: 100, opacity: 0 }}
                        onHoverStart={() => setIsHovered(true)}
                        onHoverEnd={() => setIsHovered(false)}
                        onClick={() => setIsTrayOpen(true, 'tray_button')}
                        className="fixed right-0 top-1/3 z-50 bg-alpine-gold text-black rounded-l-xl shadow-[0_4px_20px_rgba(0,0,0,0.3)] flex items-center justify-start transition-all duration-300 border-y border-l border-white/20 border-r-0"
                    >
                        <div className="flex items-center gap-3 pl-4 pr-3 py-3">
                            <motion.span
                                initial={{ width: 0, opacity: 0, marginRight: 0 }}
                                animate={{
                                    width: isHovered ? "auto" : 0,
                                    opacity: isHovered ? 1 : 0,
                                    marginRight: isHovered ? 8 : 0
                                }}
                                className="font-playfair text-xs font-bold uppercase tracking-widest whitespace-nowrap overflow-hidden"
                            >
                                {t.title || "Day Guest Pass"}
                            </motion.span>
                            <div className="relative shrink-0">
                                <CalendarIcon className="w-6 h-6" />
                            </div>
                        </div>
                    </motion.button>
                )}
            </AnimatePresence>

            <Sheet open={isTrayOpen} onOpenChange={handleTrayClose}>
                <SheetContent side="right" className="w-full sm:w-[500px] border-l border-white/10 bg-black/80 backdrop-blur-xl p-0 shadow-2xl z-[100]">
                    <div className="h-full flex flex-col relative overflow-hidden">
                        {/* Background Texture */}
                        <div className="absolute inset-0 bg-[#0f1115] opacity-95 z-[-1]" />
                        <div className="absolute inset-0 bg-[url('/images/texture-noise.png')] opacity-[0.03] z-[-1] pointer-events-none" />

                        <SheetHeader className="p-6 border-b border-white/10 relative">
                            <div className="flex items-center justify-between">
                                <div className="flex items-center gap-3">
                                    <IconMassage className="w-6 h-6 text-alpine-gold" />
                                    <SheetTitle className="text-white font-playfair text-xl tracking-wide uppercase">
                                        {t.title || "Day Guest Pass"}
                                    </SheetTitle>
                                </div>
                                <button
                                    onClick={() => handleTrayClose(false)}
                                    className="w-8 h-8 rounded-full bg-white/5 hover:bg-white/10 flex items-center justify-center transition-colors"
                                >
                                    <X className="w-4 h-4 text-white/70" />
                                </button>
                            </div>

                            {/* View Navigation */}
                            {view !== 'overview' && (
                                <button
                                    onClick={() => handleViewChange('overview')}
                                    className="mt-4 flex items-center gap-2 text-white/60 hover:text-alpine-gold transition-colors text-sm"
                                >
                                    <ArrowLeft className="w-4 h-4" />
                                    {t.backToOverview || "Back to Overview"}
                                </button>
                            )}
                        </SheetHeader>

                        <AnimatePresence mode="wait">
                            {view === 'overview' && (
                                <motion.div
                                    key="overview-view"
                                    initial={{ opacity: 0, x: -20 }}
                                    animate={{ opacity: 1, x: 0 }}
                                    exit={{ opacity: 0, x: -20 }}
                                    className="flex-1 flex flex-col min-h-0 overflow-hidden"
                                >
                                    <div className="flex-1 overflow-y-auto custom-scrollbar p-6 space-y-6">
                                        {/* Day Pass Details */}
                                        <div className="bg-white/5 border border-white/10 rounded-xl p-6 space-y-4">
                                            <div className="flex items-baseline justify-between">
                                                <h3 className="text-white font-playfair text-lg">
                                                    {t.dayPassDetails || "Day Guest Pass"}
                                                </h3>
                                                <span className="text-alpine-gold font-bold text-xl">
                                                    CHF {200} {t.perPerson || "/ person"}
                                                </span>
                                            </div>

                                            {/* Inclusions */}
                                            <div className="space-y-3 pt-2">
                                                <div className="flex items-start gap-3">
                                                    <Check className="w-5 h-5 text-alpine-gold mt-0.5 shrink-0" />
                                                    <span className="text-white/80 text-sm">{t.inclusionAllAmenities || "Access to all resort amenities"}</span>
                                                </div>
                                                <div className="flex items-start gap-3">
                                                    <Check className="w-5 h-5 text-alpine-gold mt-0.5 shrink-0" />
                                                    <div className="flex-1">
                                                        <span className="text-white/80 text-sm">
                                                            {t.inclusionMassage || "15-minute massage"} 
                                                            {t.includedValue ? ` (${t.includedValue})` : " (CHF 40 value)"}
                                                        </span>
                                                        {guestMassageUpgrades.some(g => g.upgrade.selected) && (
                                                            <div className="text-alpine-gold text-xs mt-2 space-y-1">
                                                                {guestMassageUpgrades.map((guest, idx) => 
                                                                    guest.upgrade.selected ? (
                                                                        <div key={idx}>
                                                                            {numberOfGuests > 1 ? `Guest ${idx + 1}: ` : ''}
                                                                            {guest.upgrade.treatmentName}
                                                                        </div>
                                                                    ) : null
                                                                )}
                                                            </div>
                                                        )}
                                                    </div>
                                                </div>
                                                <div className="flex items-start gap-3">
                                                    <Check className="w-5 h-5 text-alpine-gold mt-0.5 shrink-0" />
                                                    <span className="text-white/80 text-sm">{t.inclusionMeal || "5-course Restaurant Jungfrau meal (CHF 55 value, drinks extra)"}</span>
                                                </div>
                                                <div className="flex items-start gap-3">
                                                    <Check className="w-5 h-5 text-alpine-gold mt-0.5 shrink-0" />
                                                    <span className="text-white/80 text-sm">{t.inclusionCafe || "One item + tea/coffee/non-alcoholic drink from Carbon Mountain Cafe"}</span>
                                                </div>
                                                <div className="flex items-start gap-3">
                                                    <Check className="w-5 h-5 text-alpine-gold mt-0.5 shrink-0" />
                                                    <span className="text-white/80 text-sm">{t.inclusionSauna || "Sauna package (included access)"}</span>
                                                </div>
                                            </div>
                                        </div>

                                        {/* Guest Selector */}
                                        <div className="bg-white/5 border border-white/10 rounded-xl p-6 space-y-4">
                                            <h3 className="text-white font-playfair text-lg">
                                                {t.numberOfGuests || "Number of Guests"}
                                            </h3>
                                            <div className="flex items-center justify-between">
                                                <button
                                                    onClick={handleDecrementGuests}
                                                    disabled={numberOfGuests <= 1}
                                                    className="w-10 h-10 rounded-full border border-white/20 flex items-center justify-center hover:bg-white/10 transition-colors disabled:opacity-30 disabled:cursor-not-allowed"
                                                >
                                                    <Minus className="w-4 h-4 text-white/60" />
                                                </button>
                                                <span className="text-2xl font-playfair text-alpine-gold font-bold">
                                                    {numberOfGuests}
                                                </span>
                                                <button
                                                    onClick={handleIncrementGuests}
                                                    disabled={numberOfGuests >= 12}
                                                    className="w-10 h-10 rounded-full border border-white/20 flex items-center justify-center hover:bg-white/10 transition-colors disabled:opacity-30 disabled:cursor-not-allowed"
                                                >
                                                    <Plus className="w-4 h-4 text-white/60" />
                                                </button>
                                            </div>
                                            <p className="text-white/40 text-xs text-center">
                                                {t.guestLimit || "Up to 12 guests per booking"}
                                            </p>
                                        </div>

                                        {/* Price Summary */}
                                        <div className="bg-white/5 border border-white/10 rounded-xl p-6 space-y-3">
                                            <div className="flex items-baseline justify-between">
                                                <span className="text-white/60 text-sm">{t.basePassPrice || "Base Pass"}</span>
                                                <span className="text-white font-bold">CHF {basePassPrice}</span>
                                            </div>
                                            {upgradePrice > 0 && (
                                                <div className="flex items-baseline justify-between">
                                                    <span className="text-white/60 text-sm">{t.massageUpgrade || "Massage Upgrades"}</span>
                                                    <span className="text-alpine-gold font-bold">+CHF {upgradePrice}</span>
                                                </div>
                                            )}
                                            <div className="pt-3 border-t border-white/10 flex items-baseline justify-between">
                                                <span className="text-white/80 text-lg font-bold">{t.total || "Total"}</span>
                                                <span className="text-2xl font-playfair text-alpine-gold font-bold">CHF {totalPrice}</span>
                                            </div>
                                        </div>

                                        {/* Upgrade Button */}
                                        <button
                                            onClick={() => {
                                                setSelectedGuestForUpgrade(null);
                                                handleViewChange('massageUpgrade');
                                            }}
                                            className="w-full bg-white/5 hover:bg-white/10 border border-white/20 hover:border-alpine-gold/50 text-white py-3 rounded-xl font-bold uppercase tracking-widest text-xs transition-all"
                                        >
                                            {upgradePrice > 0 
                                                ? (t.changeUpgrade || "Change Massage Upgrades")
                                                : (t.upgradeMassage || "Upgrade Massage")
                                            }
                                        </button>
                                    </div>

                                    {/* Footer */}
                                    <div className="p-6 border-t border-white/10 bg-black/60 backdrop-blur-md">
                                        <button
                                            onClick={() => handleViewChange('bookingForm')}
                                            className="w-full bg-alpine-gold text-black hover:scale-[1.02] active:scale-[0.98] transition-all py-3.5 rounded-xl font-extrabold uppercase tracking-[0.2em] text-[10px] shadow-lg flex items-center justify-center gap-2"
                                        >
                                            {t.bookNow || "Book Now"}
                                            <ArrowRight className="w-3.5 h-3.5" />
                                        </button>
                                    </div>
                                </motion.div>
                            )}

                            {view === 'massageUpgrade' && (
                                <motion.div
                                    key="massage-upgrade-view"
                                    initial={{ opacity: 0, x: 20 }}
                                    animate={{ opacity: 1, x: 0 }}
                                    exit={{ opacity: 0, x: 20 }}
                                    className="flex-1 flex flex-col min-h-0 overflow-hidden"
                                >
                                    <div className="flex-1 overflow-y-auto custom-scrollbar p-6 space-y-6">
                                        <div className="space-y-4">
                                            <h3 className="text-white font-playfair text-xl">
                                                {t.upgradeYourMassage || "Upgrade Massages"}
                                            </h3>
                                            <p className="text-white/60 text-sm">
                                                {numberOfGuests > 1 
                                                    ? (t.upgradeDescriptionMultiple || "Select upgrades for each guest. Each guest can choose a different massage treatment.")
                                                    : (t.upgradeDescription || "Choose a full spa treatment to upgrade from your included 15-minute massage.")
                                                }
                                            </p>
                                        </div>

                                        {/* Guest Selection (if multiple guests) */}
                                        {numberOfGuests > 1 && selectedGuestForUpgrade === null && (
                                            <div className="space-y-3">
                                                <h4 className="text-alpine-gold font-playfair text-base uppercase tracking-widest">
                                                    {t.selectGuest || "Select Guest to Upgrade"}
                                                </h4>
                                                {Array.from({ length: numberOfGuests }, (_, idx) => {
                                                    const guestUpgrade = getGuestMassageUpgrade(idx);
                                                    return (
                                                        <div
                                                            key={idx}
                                                            onClick={() => setSelectedGuestForUpgrade(idx)}
                                                            className="bg-white/5 border border-white/10 hover:border-alpine-gold/50 rounded-xl p-4 cursor-pointer transition-all"
                                                        >
                                                            <div className="flex items-center justify-between">
                                                                <div>
                                                                    <h4 className="text-white font-playfair">
                                                                        {t.guest || "Guest"} {idx + 1}
                                                                    </h4>
                                                                    <p className="text-white/50 text-xs mt-1">
                                                                        {guestUpgrade.selected 
                                                                            ? guestUpgrade.treatmentName 
                                                                            : (t.includedMassage || "15-minute massage (included)")
                                                                        }
                                                                    </p>
                                                                </div>
                                                                <ArrowRight className="w-4 h-4 text-white/40" />
                                                            </div>
                                                        </div>
                                                    );
                                                })}
                                            </div>
                                        )}

                                        {/* Upgrade Selection for Selected Guest */}
                                        {selectedGuestForUpgrade !== null && (
                                            <>
                                                <div className="flex items-center gap-3 mb-4">
                                                    <button
                                                        onClick={() => setSelectedGuestForUpgrade(null)}
                                                        className="w-8 h-8 rounded-full border border-white/20 flex items-center justify-center hover:bg-white/10 transition-colors"
                                                    >
                                                        <ArrowLeft className="w-4 h-4 text-white/60" />
                                                    </button>
                                                    <h4 className="text-white font-playfair">
                                                        {t.selectingForGuest || "Selecting for"} {t.guest || "Guest"} {selectedGuestForUpgrade + 1}
                                                    </h4>
                                                </div>

                                                {/* Included Option */}
                                                <div
                                                    className={`bg-white/5 border rounded-xl p-4 cursor-pointer transition-all ${
                                                        !getGuestMassageUpgrade(selectedGuestForUpgrade).selected
                                                            ? 'border-alpine-gold bg-alpine-gold/10'
                                                            : 'border-white/10 hover:border-alpine-gold/30'
                                                    }`}
                                                    onClick={() => {
                                                        clearGuestMassageUpgrade(selectedGuestForUpgrade);
                                                        handleMassageUpgrade('clear', selectedGuestForUpgrade);
                                                        toast.success(t.includedSelected || 'Included massage selected');
                                                    }}
                                                >
                                                    <div className="flex items-center justify-between">
                                                        <div>
                                                            <h4 className="text-white font-playfair">
                                                                {t.includedMassage || "15-minute massage (included)"}
                                                            </h4>
                                                            <p className="text-white/50 text-xs mt-1">
                                                                {t.includedValue ? `(${t.includedValue})` : "(CHF 40 value)"}
                                                            </p>
                                                        </div>
                                                        {!getGuestMassageUpgrade(selectedGuestForUpgrade).selected && (
                                                            <div className="w-6 h-6 rounded-full bg-alpine-gold flex items-center justify-center">
                                                                <Check className="w-4 h-4 text-black" />
                                                            </div>
                                                        )}
                                                    </div>
                                                </div>

                                                {/* Upgrade Options */}
                                                <div className="space-y-4">
                                                    <h4 className="text-alpine-gold font-playfair text-base uppercase tracking-widest">
                                                        {t.upgradeOptions || "Upgrade Options"}
                                                    </h4>
                                                    {massageServices.map((service) => (
                                                        <MassageUpgradeItem 
                                                            key={service.serviceId} 
                                                            service={service} 
                                                            guestIndex={selectedGuestForUpgrade}
                                                            onUpgradeSelect={(treatmentId, treatmentName, treatmentPrice, upgradePrice) => {
                                                                handleMassageUpgrade('select', selectedGuestForUpgrade, treatmentId, treatmentName, treatmentPrice, upgradePrice);
                                                            }}
                                                        />
                                                    ))}
                                                </div>
                                            </>
                                        )}

                                        {/* Single Guest Upgrade (when only 1 guest) */}
                                        {numberOfGuests === 1 && selectedGuestForUpgrade === null && (
                                            <>
                                                {/* Included Option */}
                                                <div
                                                    className={`bg-white/5 border rounded-xl p-4 cursor-pointer transition-all ${
                                                        !getGuestMassageUpgrade(0).selected
                                                            ? 'border-alpine-gold bg-alpine-gold/10'
                                                            : 'border-white/10 hover:border-alpine-gold/30'
                                                    }`}
                                                    onClick={() => {
                                                        clearGuestMassageUpgrade(0);
                                                        handleMassageUpgrade('clear', 0);
                                                        toast.success(t.includedSelected || 'Included massage selected');
                                                    }}
                                                >
                                                    <div className="flex items-center justify-between">
                                                        <div>
                                                            <h4 className="text-white font-playfair">
                                                                {t.includedMassage || "15-minute massage (included)"}
                                                            </h4>
                                                            <p className="text-white/50 text-xs mt-1">
                                                                {t.includedValue ? `(${t.includedValue})` : "(CHF 40 value)"}
                                                            </p>
                                                        </div>
                                                        {!getGuestMassageUpgrade(0).selected && (
                                                            <div className="w-6 h-6 rounded-full bg-alpine-gold flex items-center justify-center">
                                                                <Check className="w-4 h-4 text-black" />
                                                            </div>
                                                        )}
                                                    </div>
                                                </div>

                                                {/* Upgrade Options */}
                                                <div className="space-y-4">
                                                    <h4 className="text-alpine-gold font-playfair text-base uppercase tracking-widest">
                                                        {t.upgradeOptions || "Upgrade Options"}
                                                    </h4>
                                                    {massageServices.map((service) => (
                                                        <MassageUpgradeItem 
                                                            key={service.serviceId} 
                                                            service={service} 
                                                            guestIndex={0}
                                                            onUpgradeSelect={(treatmentId, treatmentName, treatmentPrice, upgradePrice) => {
                                                                handleMassageUpgrade('select', 0, treatmentId, treatmentName, treatmentPrice, upgradePrice);
                                                            }}
                                                        />
                                                    ))}
                                                </div>
                                            </>
                                        )}
                                    </div>

                                    <div className="p-6 border-t border-white/10 bg-black/60 backdrop-blur-md">
                                        <button
                                            onClick={() => handleViewChange('overview')}
                                            className="w-full bg-alpine-gold text-black hover:scale-[1.02] active:scale-[0.98] transition-all py-3.5 rounded-xl font-extrabold uppercase tracking-[0.2em] text-[10px] shadow-lg"
                                        >
                                            {t.confirmUpgrade || "Confirm Selection"}
                                        </button>
                                    </div>
                                </motion.div>
                            )}

                            {view === 'bookingForm' && (
                                <motion.div
                                    key="booking-form-view"
                                    initial={{ opacity: 0, x: 20 }}
                                    animate={{ opacity: 1, x: 0 }}
                                    exit={{ opacity: 0, x: 20 }}
                                    className="flex-1 flex flex-col min-h-0 overflow-hidden"
                                >
                                    <div className="flex-1 overflow-y-auto custom-scrollbar p-6 space-y-6">
                                        <div className="space-y-4">
                                            <h3 className="text-white font-playfair text-xl">
                                                {t.bookingInformation || "Booking Information"}
                                            </h3>
                                            <p className="text-white/60 text-sm">
                                                {t.bookingDescription || "Please provide your details to complete your Day Guest Pass booking."}
                                            </p>
                                        </div>

                                        {submitStatus === 'success' ? (
                                            <div className="bg-green-500/10 border border-green-500/20 rounded-xl p-6 text-center">
                                                <p className="text-green-400 font-playfair italic text-lg">
                                                    {t.thankYouReceived || "Thank you! Your booking request has been received."}
                                                </p>
                                                <p className="text-white/60 text-sm mt-2">
                                                    {t.confirmationEmail || "We will send you a confirmation email shortly."}
                                                </p>
                                            </div>
                                        ) : (
                                            <form onSubmit={handleSubmit} className="space-y-4">
                                                <div className="space-y-2">
                                                    <label className="text-[9px] uppercase tracking-widest text-white/40 font-bold pl-1">
                                                        {t.numberOfGuests || "Number of Guests"} *
                                                    </label>
                                                    <div className="flex items-center justify-between bg-white/5 border border-white/10 rounded-lg px-4 py-3">
                                                        <button
                                                            type="button"
                                                            onClick={handleDecrementGuests}
                                                            disabled={numberOfGuests <= 1}
                                                            className="w-8 h-8 rounded-full border border-white/20 flex items-center justify-center hover:bg-white/10 transition-colors disabled:opacity-30"
                                                        >
                                                            <Minus className="w-3 h-3 text-white/60" />
                                                        </button>
                                                        <span className="text-white font-bold">{numberOfGuests}</span>
                                                        <button
                                                            type="button"
                                                            onClick={handleIncrementGuests}
                                                            disabled={numberOfGuests >= 12}
                                                            className="w-8 h-8 rounded-full border border-white/20 flex items-center justify-center hover:bg-white/10 transition-colors disabled:opacity-30"
                                                        >
                                                            <Plus className="w-3 h-3 text-white/60" />
                                                        </button>
                                                    </div>
                                                </div>

                                                <input
                                                    type="text"
                                                    name="fullName"
                                                    placeholder={`${t.fullName || "Full Name"} *`}
                                                    required
                                                    value={formData.fullName}
                                                    onChange={handleInputChange}
                                                    className="w-full bg-white/5 border border-white/10 rounded-lg px-3 py-2.5 text-xs text-white placeholder:text-white/20 focus:outline-none focus:border-alpine-gold/50 transition-colors"
                                                />
                                                <input
                                                    type="email"
                                                    name="email"
                                                    placeholder={`${t.email || "Email"} *`}
                                                    required
                                                    value={formData.email}
                                                    onChange={handleInputChange}
                                                    className="w-full bg-white/5 border border-white/10 rounded-lg px-3 py-2.5 text-xs text-white placeholder:text-white/20 focus:outline-none focus:border-alpine-gold/50 transition-colors"
                                                />
                                                <input
                                                    type="tel"
                                                    name="phone"
                                                    placeholder={`${t.phone || "Phone"} *`}
                                                    required
                                                    minLength={5}
                                                    value={formData.phone}
                                                    onChange={handleInputChange}
                                                    className="w-full bg-white/5 border border-white/10 rounded-lg px-3 py-2.5 text-xs text-white placeholder:text-white/20 focus:outline-none focus:border-alpine-gold/50 transition-colors"
                                                />

                                                <div className="grid grid-cols-2 gap-3">
                                                    <div className="space-y-1.5">
                                                        <label className="text-[9px] uppercase tracking-widest text-white/40 font-bold pl-1">
                                                            {t.preferredDate || "Preferred Date"} *
                                                        </label>
                                                        <Popover>
                                                            <PopoverTrigger asChild>
                                                                <Button
                                                                    type="button"
                                                                    variant="outline"
                                                                    className={`w-full justify-start text-left font-normal bg-white/5 border border-white/10 rounded-lg px-3 py-2.5 text-xs text-white/80 hover:bg-white/10 focus:outline-none focus:border-alpine-gold/50 transition-colors ${!formData.preferredDate ? 'text-white/40' : ''}`}
                                                                >
                                                                    <CalendarIcon className="mr-2 h-3.5 w-3.5" />
                                                                    {formData.preferredDate
                                                                        ? format(new Date(formData.preferredDate), "MMM dd, yyyy")
                                                                        : (t.selectDate || "Select Date")}
                                                                </Button>
                                                            </PopoverTrigger>
                                                            <PopoverContent className="w-auto p-0 bg-black/95 border-white/10 z-[200]" align="start" sideOffset={4}>
                                                                <Calendar
                                                                    mode="single"
                                                                    selected={formData.preferredDate ? (() => {
                                                                        const dateStr = formData.preferredDate;
                                                                        const [year, month, day] = dateStr.split('-').map(Number);
                                                                        return new Date(year, month - 1, day);
                                                                    })() : undefined}
                                                                    onSelect={handleDateSelect}
                                                                    disabled={(date) => {
                                                                        const today = getTodayAtMidnight();
                                                                        const dateToCheck = new Date(date);
                                                                        dateToCheck.setHours(0, 0, 0, 0);
                                                                        return dateToCheck < today;
                                                                    }}
                                                                    initialFocus
                                                                    className="bg-transparent text-white"
                                                                    classNames={{
                                                                        months: "flex flex-col sm:flex-row space-y-4 sm:space-x-4 sm:space-y-0",
                                                                        month: "space-y-4",
                                                                        caption: "flex justify-center pt-1 relative items-center",
                                                                        caption_label: "text-sm font-medium text-alpine-gold",
                                                                        nav: "space-x-1 flex items-center",
                                                                        nav_button: "h-7 w-7 bg-transparent p-0 opacity-50 hover:opacity-100 text-white hover:text-alpine-gold border border-white/20 hover:border-alpine-gold/50 rounded-md",
                                                                        nav_button_previous: "absolute left-1",
                                                                        nav_button_next: "absolute right-1",
                                                                        table: "w-full border-collapse space-y-1",
                                                                        head_row: "flex",
                                                                        head_cell: "text-white/60 rounded-md w-9 font-normal text-[0.8rem]",
                                                                        row: "flex w-full mt-2",
                                                                        cell: "h-9 w-9 text-center text-sm p-0 relative",
                                                                        day: "h-9 w-9 p-0 font-normal text-white hover:bg-alpine-gold/20 hover:text-alpine-gold rounded-md transition-colors",
                                                                        day_selected: "bg-alpine-gold text-black hover:bg-alpine-gold hover:text-black focus:bg-alpine-gold focus:text-black font-semibold",
                                                                        day_today: "bg-white/10 text-alpine-gold border border-alpine-gold/50 font-semibold",
                                                                        day_outside: "text-white/30 opacity-50",
                                                                        day_disabled: "text-white/20 opacity-30 cursor-not-allowed",
                                                                        day_range_middle: "bg-alpine-gold/20 text-alpine-gold",
                                                                        day_hidden: "invisible",
                                                                    }}
                                                                />
                                                            </PopoverContent>
                                                        </Popover>
                                                    </div>
                                                    <div className="space-y-1.5">
                                                        <label className="text-[9px] uppercase tracking-widest text-white/40 font-bold pl-1">
                                                            {t.preferredTime || "Preferred Time"} *
                                                        </label>
                                                        <select
                                                            name="preferredTime"
                                                            value={formData.preferredTime}
                                                            onChange={handleInputChange}
                                                            required
                                                            className="bg-white/5 border border-white/10 rounded-lg px-3 py-2.5 text-xs text-white/80 focus:outline-none focus:border-alpine-gold/50 transition-colors w-full"
                                                        >
                                                            <option value="Morning">{t.morning || "Morning"}</option>
                                                            <option value="Afternoon">{t.afternoon || "Afternoon"}</option>
                                                            <option value="Evening">{t.evening || "Evening"}</option>
                                                        </select>
                                                    </div>
                                                </div>

                                                <textarea
                                                    name="specialRequests"
                                                    placeholder={t.specialRequests || "Special Requests (Optional)"}
                                                    value={formData.specialRequests}
                                                    onChange={handleInputChange}
                                                    rows={3}
                                                    className="w-full bg-white/5 border border-white/10 rounded-lg px-3 py-2.5 text-xs text-white placeholder:text-white/20 focus:outline-none focus:border-alpine-gold/50 transition-colors resize-none"
                                                />

                                                {/* Booking Summary */}
                                                <div className="bg-white/5 border border-white/10 rounded-xl p-4 space-y-2">
                                                    <h4 className="text-white font-playfair text-sm mb-3">
                                                        {t.bookingSummary || "Booking Summary"}
                                                    </h4>
                                                    <div className="flex items-baseline justify-between text-sm">
                                                        <span className="text-white/60">{t.basePassPrice || "Base Pass"}</span>
                                                        <span className="text-white">CHF {basePassPrice}</span>
                                                    </div>
                                                    {upgradePrice > 0 && (
                                                        <div className="flex items-baseline justify-between text-sm">
                                                            <span className="text-white/60">{t.massageUpgrade || "Massage Upgrades"}</span>
                                                            <span className="text-alpine-gold">+CHF {upgradePrice}</span>
                                                        </div>
                                                    )}
                                                    {numberOfGuests > 1 && upgradePrice > 0 && (
                                                        <div className="text-white/40 text-xs mt-2 space-y-1">
                                                            {guestMassageUpgrades.map((guest, idx) => 
                                                                guest.upgrade.selected ? (
                                                                    <div key={idx}>
                                                                        {t.guest || "Guest"} {idx + 1}: {guest.upgrade.treatmentName} (+CHF {guest.upgrade.upgradePrice})
                                                                    </div>
                                                                ) : null
                                                            )}
                                                        </div>
                                                    )}
                                                    <div className="pt-2 border-t border-white/10 flex items-baseline justify-between">
                                                        <span className="text-white font-bold">{t.total || "Total"}</span>
                                                        <span className="text-alpine-gold font-bold text-lg">CHF {totalPrice}</span>
                                                    </div>
                                                </div>

                                                <p className="text-[10px] text-white/30 italic leading-relaxed">
                                                    {t.requestedTimesNote || "*Requested times are subject to availability. We will contact you to confirm your booking."}
                                                </p>

                                                <button
                                                    type="submit"
                                                    disabled={isSubmitting}
                                                    className="w-full bg-alpine-gold text-black hover:scale-[1.02] active:scale-[0.98] transition-all py-3.5 rounded-xl font-extrabold uppercase tracking-[0.2em] text-[10px] shadow-lg disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                                                >
                                                    {isSubmitting ? (
                                                        <span className="w-4 h-4 border-2 border-black/30 border-t-black rounded-full animate-spin" />
                                                    ) : (
                                                        <>
                                                            {t.submitBooking || "Submit Booking Request"}
                                                            <ArrowRight className="w-3.5 h-3.5" />
                                                        </>
                                                    )}
                                                </button>
                                            </form>
                                        )}
                                    </div>
                                </motion.div>
                            )}
                        </AnimatePresence>
                    </div>
                </SheetContent>
            </Sheet>
        </>
    );
};

